import { useCallback, useEffect, useRef } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';
import type {
	PhoneControllerFieldProps,
	UsePhoneControllerArgs
} from './types';

export function usePhoneController<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
>({
	clearErrors,
	control,
	invalidMessage = 'Invalid phone number format',
	name,
	revalidateOn,
	setError
}: UsePhoneControllerArgs<
	TFieldValues,
	TName,
	TTransformedValues
>): PhoneControllerFieldProps {
	const valueRef = useRef('');
	const validityRef = useRef<boolean | undefined>(undefined);

	const { field, fieldState } = useController({
		control,
		name,
		rules: {
			validate: (value) => {
				const fieldValue = typeof value === 'string' ? value.trim() : '';

				if (!fieldValue || validityRef.current !== false) {
					return true;
				}

				return invalidMessage;
			}
		}
	});

	const fieldValue = typeof field.value === 'string' ? field.value : '';
	valueRef.current = fieldValue;

	const latestRef = useRef({ field, fieldState, revalidateOn });
	latestRef.current.field = field;
	latestRef.current.fieldState = fieldState;

	useEffect(() => {
		const latest = latestRef.current;

		if (latest.revalidateOn === revalidateOn) {
			return;
		}
		latest.revalidateOn = revalidateOn;

		if (!valueRef.current.trim() || !latest.fieldState.error) {
			return;
		}

		latest.field.onChange(valueRef.current);
	}, [revalidateOn]);

	const onChange = useCallback(
		(value: string) => {
			if (valueRef.current === value) {
				return;
			}

			valueRef.current = value;

			field.onChange(value);
		},
		[field.onChange]
	);

	const onChangeValidity = useCallback(
		(isValid: boolean) => {
			const prev = validityRef.current;

			if (prev === isValid) {
				return;
			}

			validityRef.current = isValid;

			if (valueRef.current.trim() && clearErrors && setError) {
				if (isValid) {
					clearErrors(name);
				} else {
					setError(name, {
						message: invalidMessage,
						type: 'validate'
					});
				}
			}

			// re-trigger resolver after intl-tel-input reports validity change
			if (prev !== undefined && valueRef.current.trim()) {
				field.onChange(valueRef.current);
			}
		},
		[clearErrors, field.onChange, invalidMessage, name, setError]
	);

	return {
		name: field.name,
		value: (field.value as string) ?? '',
		error: fieldState.error?.message,
		onBlur: field.onBlur,
		onChange,
		onChangeValidity
	};
}
